import { formatISO } from "date-fns";
import { ComponentProps, Suspense, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useWatch } from "react-hook-form";
import {
  useLazyLoadQuery,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { Mutation, Query } from ".";
import PublishForm from "./form";
import { AuthorSchemaInput, Schema, schema } from "./schema";
import { fetchAllTags } from "@/utils/taggable";
import { fetchAllAuthors } from "@/utils/dataset";
import { logger } from "@/common/logger";
import { Loading01Icon } from "@/icons";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Alert } from "@/kit/ui/alert";
import { Button } from "@/kit/ui/button";
import { datasetVersionCitationFragment$key } from "@/utils/__generated__/datasetVersionCitationFragment.graphql";
import { formatDatasetVersionCitation } from "@/utils/dataset";
import { relayErrorMessage } from "@/utils/relay";
import { useValibotForm, useValibotFormContext } from "@/utils/validation";
import { assertNever, invariant } from "@/utils/helpers";
import { toast } from "@/utils/toast";
import { DatasetVersionPublishButtonQuery } from "./__generated__/DatasetVersionPublishButtonQuery.graphql";
import { DatasetVersionPublishButtonMutation } from "./__generated__/DatasetVersionPublishButtonMutation.graphql";
import { cn } from "@/utils/tailwind";
import { taggableFragment$key } from "@/utils/__generated__/taggableFragment.graphql";
import { SlotContext, useOnceSlot } from "@/utils/suspense";

export interface PublishModalProps extends ComponentProps<
  typeof ResponsiveModalTrigger
> {
  datasetVersionId: ID;
}

export function PublishModal(props: PublishModalProps) {
  const { datasetVersionId, ...triggerProps } = props;

  const [isOpen, setOpen] = useState(false);

  return (
    <ResponsiveModal open={isOpen} onOpenChange={setOpen}>
      <ResponsiveModalTrigger {...triggerProps} />
      <ResponsiveModalPortal>
        <ResponsiveModalContent
          className="p-0 gap-0 overflow-hidden flex flex-col w-[80vw] lg:w-[600px] max-h-[80%] lg:max-h-[67%]"
          noClose
        >
          <ResponsiveModalHeader className="flex-none px-4 py-3 border-b border-grey-200 lg:border-0 lg:p-6">
            <ResponsiveModalTitle className="text-start text-xl">
              <FormattedMessage defaultMessage="Publish dataset version" />
            </ResponsiveModalTitle>
          </ResponsiveModalHeader>

          <Suspense fallback={<LoadingFormAlert className="mx-4 my-3" />}>
            <ModalContent
              datasetVersionId={datasetVersionId}
              onOpenChange={setOpen}
            />
          </Suspense>
        </ResponsiveModalContent>
      </ResponsiveModalPortal>
    </ResponsiveModal>
  );
}

interface ModalContentProps {
  datasetVersionId: ID;
  onOpenChange: (isOpen: boolean) => void;
}

function ModalContent({ datasetVersionId, onOpenChange }: ModalContentProps) {
  const relay = useRelayEnvironment();
  const intl = useIntl();
  const {
    node: { datasetVersion },
  } = useLazyLoadQuery<DatasetVersionPublishButtonQuery>(Query, {
    datasetVersionId,
  });

  const [mutation, isMutationInFlight] =
    useMutation<DatasetVersionPublishButtonMutation>(Mutation);

  const form = useValibotForm(schema(intl), {
    mode: "onChange",

    async defaultValues() {
      invariant(!!datasetVersion);

      const authors = await fetchAllAuthors(relay, datasetVersion);

      return {
        license: datasetVersion.license,
        authors: authors.map(({ node }): AuthorSchemaInput => {
          invariant(node.__typename !== "%other");
          switch (node.__typename) {
            case "User":
              return {
                registered: { id: node.id, displayName: node.displayName },
              };
            case "UnregisteredUser":
              return { unregistered: { fullName: node.fullName } };
            default:
              assertNever(node);
          }
        }),
      };
    },
  });

  const doSubmit = form.handleSubmit((data) => {
    mutation({
      variables: {
        id: datasetVersionId,
        input: {
          ...data,
          authors: data.authors?.map((author) => {
            if ("registered" in author) {
              return { registered: author.registered.id };
            } else {
              return author;
            }
          }),
        },
      },
      onCompleted() {
        toast.success(
          <FormattedMessage defaultMessage="Dataset version published!" />,
        );
        onOpenChange(false);
      },
      onError(error) {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });
  });

  if (!datasetVersion) {
    throw new Error(`No such dataset version ${datasetVersionId}`);
  }

  return (
    <Form
      form={form}
      onSubmit={doSubmit}
      aria-disabled={isMutationInFlight || form.formState.isLoading}
      className={cn("flex-1 flex flex-col overflow-hidden")}
    >
      <div className="flex-1 flex flex-col gap-6 px-4 py-3 overflow-y-auto lg:px-6 lg:py-0 lg:pb-6">
        <ResponsiveModalDescription asChild>
          {form.formState.isLoading ? (
            <LoadingFormAlert />
          ) : (
            <Alert
              kind="warning"
              header={
                <FormattedMessage defaultMessage="Final Check Before Publishing" />
              }
            >
              <FormattedMessage defaultMessage="Once published, you cannot modify the contents, but metadata can still be updated." />
            </Alert>
          )}
        </ResponsiveModalDescription>

        <div
          className={cn(
            "flex flex-col gap-6",
            form.formState.isLoading && "opacity-50 pointer-events-none",
          )}
        >
          <PublishForm />
        </div>

        {datasetVersion.dataset.private ? (
          <Alert
            kind="info"
            header={
              <FormattedMessage defaultMessage="This dataset is private!" />
            }
          >
            <FormattedMessage defaultMessage="Only selected users will see this version after publishing. Make the dataset public if you want everyone to access it." />
          </Alert>
        ) : (
          <SlotContext>
            <Suspense
              fallback={<CitationSkeleton datasetVersion={datasetVersion} />}
            >
              <Citation
                datasetVersion={datasetVersion}
                dataset={datasetVersion.dataset}
              />
            </Suspense>
          </SlotContext>
        )}
      </div>

      <ResponsiveModalFooter className="flex-none flex-row p-4 border-t border-grey-60 lg:border-none">
        <ResponsiveModalClose asChild>
          <Button variant="secondary" className="flex-1 lg:flex-initial">
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
        </ResponsiveModalClose>

        <Button
          type="submit"
          disabled={
            form.formState.isLoading ||
            !form.formState.isValid ||
            isMutationInFlight
          }
          className="flex-1 lg:flex-initial"
        >
          {isMutationInFlight && (
            <Loading01Icon className="w-5 h-5 animate-spin" />
          )}
          <FormattedMessage defaultMessage="Publish dataset" />
        </Button>
      </ResponsiveModalFooter>
    </Form>
  );
}

interface CitationBaseProps {
  citation: string;
  isLoading?: boolean | undefined;
}

function CitationBase({ citation, isLoading }: CitationBaseProps) {
  return (
    <FormItem className={cn(isLoading && "opacity-50")}>
      <FormLabel className="flex gap-1 items-center">
        <FormattedMessage defaultMessage="Citation" />
        {isLoading && <Loading01Icon className="animate-spin w-4 h-4" />}
      </FormLabel>

      <FormControl>
        <blockquote className="px-4 py-3 whitespace-pre bg-grey-50 rounded-xl border border-grey-200 text-sm text-grey-900 font-mono font-normal overflow-x-auto shrink-0">
          {citation}
        </blockquote>
      </FormControl>

      <FormMessage className="text-grey-600">
        <FormattedMessage defaultMessage="Example BibTeX for preview only. Use the official citation after publication." />
      </FormMessage>
    </FormItem>
  );
}

interface CitationProps {
  datasetVersion: datasetVersionCitationFragment$key;
  dataset: taggableFragment$key;
}

function Citation({ datasetVersion, dataset }: CitationProps) {
  return (
    <CitationBase
      citation={useCitation(datasetVersion, useDatasetTags(dataset))}
    />
  );
}

interface CitationSkeletonProps {
  datasetVersion: datasetVersionCitationFragment$key;
}

function CitationSkeleton({ datasetVersion }: CitationSkeletonProps) {
  return <CitationBase citation={useCitation(datasetVersion)} isLoading />;
}

function useDatasetTags(dataset: taggableFragment$key) {
  const relay = useRelayEnvironment();
  return useOnceSlot(() => fetchAllTags(relay, dataset)).map(
    ({ node }) => node.name,
  );
}

function useCitation(
  datasetVersion: datasetVersionCitationFragment$key,
  tags: string[] = [],
) {
  const { control } = useValibotFormContext<Schema>();
  const [license, authors] = useWatch({
    control,
    name: ["license", "authors"],
  });
  const citation = formatDatasetVersionCitation(
    datasetVersion,
    showAuthors(authors ?? []),
    tags,
    {
      publishedAt: formatISO(new Date()),
      doi: {
        full: "YYMM.00000",
      },
      license,
    },
  );
  return citation;
}

function showAuthors(authors: AuthorSchemaInput[]): string[] {
  return authors.map((author) => {
    if ("registered" in author) {
      return author.registered.displayName;
    } else {
      return author.unregistered.fullName;
    }
  });
}

const LoadingFormAlert = (props: ComponentProps<"div">) => (
  <Alert
    kind="info"
    header={<FormattedMessage defaultMessage="Form is loading" />}
    asChild
    {...props}
  >
    <div className="flex gap-1 items-center">
      <p>
        <FormattedMessage defaultMessage="Please wait" />
      </p>
      <Loading01Icon className="animate-spin w-4 h-4" />
    </div>
  </Alert>
);
